import { type JSONData } from "../../base_classes/dto.d";
import { AirlineOrderGroupDto } from "./dto.gen";
import { type IAirlineOrderGroup } from "./entity.d";
import AirlineOrderGroupParsers from "./parsers";
import AirlineOrderGroupValidators from "./custom_validators";
import { AirlineOrder, AirlinePricing, AirlineSettings } from "..";
import { Exclude } from "class-transformer";

export class AirlineOrderGroup
  extends AirlineOrderGroupDto
  implements IAirlineOrderGroup
{
  constructor(
    pathVars?:
      | string
      | string[]
      | {
          id?: string;
        },
    data?: JSONData,
  ) {
    super(data, AirlineOrderGroupParsers, AirlineOrderGroupValidators);

    if (data !== undefined) {
      // Set data
      this.set(data);
    }

    this.buildPathObject(pathVars);
  }

  /// ///////////////////////////////////////
  /// ////////////// METHODS ////////////////
  /// ///////////////////////////////////////

  /**
   * Override set to deserialize JSON strings before calling parent
   */
  override set(data: JSONData): void {
    // Deserialize orders from JSON string if needed
    if (data.orders && typeof data.orders === 'string') {
      console.log("AirlineOrderGroup.set: orders is a string, deserializing...");
      try {
        data.orders = JSON.parse(data.orders);
        console.log("AirlineOrderGroup.set: deserialized orders:", data.orders);
      } catch (e) {
        console.error('Failed to deserialize orders in set():', e);
        data.orders = [];
      }
    }

    // Deserialize pricingTables from JSON string if needed
    if (data.pricingTables && typeof data.pricingTables === 'string') {
      try {
        data.pricingTables = JSON.parse(data.pricingTables);
      } catch (e) {
        console.error('Failed to deserialize pricingTables in set():', e);
        data.pricingTables = [];
      }
    }

    // Deserialize settings from JSON string if needed
    if (data.settings && typeof data.settings === 'string') {
      try {
        data.settings = JSON.parse(data.settings);
      } catch (e) {
        console.error('Failed to deserialize settings in set():', e);
        data.settings = {};
      }
    }

    // Store computed values from data if they exist (for historical data preservation)
    // These will be stored as private properties to avoid conflicts with getters
    const historicalValues = {
      _storedMarginAmount: data.marginAmount,
      _storedMarginAmountPerUnit: data.marginAmountPerUnit,
      _storedMarginPercentage: data.marginPercentage,
      _storedMaxColors: data.maxColors,
      _storedMaxWidth: data.maxWidth,
      _storedDividers: data.dividers,
      _storedOriginalTotalCosts: data.originalTotalCosts,
      _storedSavedCosts: data.savedCosts,
      _storedSubtotalCosts: data.subtotalCosts,
      _storedSwitchCosts: data.switchCosts,
      _storedTotalCosts: data.totalCosts,
      _storedProfitAmount: data.profitAmount,
      _storedProfitPercentage: data.profitPercentage,
      _storedTotalSalePrice: data.totalSalePrice,
      _storedUnits: data.units,
      _storedUnitCosts: data.unitCosts,
      _storedTotalBaseFoilRollAmount: data.totalBaseFoilRollAmount,
      _storedBaseFoilSKU: data.baseFoilSKU,
    };

    // Remove computed getter properties from data before calling parent set
    // These properties are calculated dynamically and should not be set from data
    const {
      marginAmount,
      marginAmountPerUnit,
      marginPercentage,
      maxColors,
      maxWidth,
      dividers,
      originalTotalCosts,
      savedCosts,
      subtotalCosts,
      switchCosts,
      totalCosts,
      profitAmount,
      profitPercentage,
      totalSalePrice,
      units,
      unitCosts,
      totalBaseFoilRollAmount,
      baseFoilSKU,
      ...cleanData
    } = data;

    // Call parent set with deserialized and cleaned data
    super.set(cleanData);

    // Apply historical values if they were present in the data
    Object.entries(historicalValues).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        (this as any)[key] = value;
      }
    });
  }

  /// ///////////////////////////////////////
  /// //////// PRIVATE VARIABLES ////////////
  /// ///////////////////////////////////////
  /**
   * The pricing tables
   */
  @Exclude()
  pricingTables: AirlinePricing[] = [];

  /**
   * The airline settings
   */
  @Exclude()
  settings: AirlineSettings = new AirlineSettings();

  // Private storage for historical computed values
  private _storedMarginAmount?: number;
  private _storedMarginAmountPerUnit?: number;
  private _storedMarginPercentage?: number;
  private _storedMaxColors?: number;
  private _storedMaxWidth?: number;
  private _storedDividers?: number;
  private _storedOriginalTotalCosts?: number;
  private _storedSavedCosts?: number;
  private _storedSubtotalCosts?: number;
  private _storedSwitchCosts?: number;
  private _storedTotalCosts?: number;
  private _storedProfitAmount?: number;
  private _storedProfitPercentage?: number;
  private _storedTotalSalePrice?: number;
  private _storedUnits?: number;
  private _storedUnitCosts?: number;
  private _storedTotalBaseFoilRollAmount?: number;
  private _storedBaseFoilSKU?: string;

  /// ///////////////////////////////////////
  /// ///////// PUBLIC VARIABLES ////////////
  /// ///////////////////////////////////////

  /**
   * The maximum number of dividers within the group
   */
  get dividers() {
    // Return stored value if available (historical data)
    if (this._storedDividers !== undefined) return this._storedDividers;
    // Otherwise calculate
    if (!this.orders || this.orders.length === 0) return 0;
    return Math.max(...this.orders.map((o: AirlineOrder) => o.dividers));
  }

  /**
   * The total margin amount of the order group
   */
  get marginAmount() {
    if (this._storedMarginAmount !== undefined) return this._storedMarginAmount;
    return this.orders.reduce(
      (total: number, order: AirlineOrder) => total + order.margin.amount,
      0,
    );
  }

  /**
   * The total margin amount of the order group
   */
  get marginAmountPerUnit() {
    if (this._storedMarginAmountPerUnit !== undefined) return this._storedMarginAmountPerUnit;
    return this.marginAmount / this.units;
  }

  /**
   * The total margin amount of the order group
   */
  get marginPercentage() {
    if (this._storedMarginPercentage !== undefined) return this._storedMarginPercentage;
    return (
      (this.marginAmount / (this.marginAmount + this.originalTotalCosts)) * 100
    );
  }

  /**
   * The maximum number of colors within the group
   */
  get maxColors() {
    if (this._storedMaxColors !== undefined) return this._storedMaxColors;
    if (!this.orders || this.orders.length === 0) return 0;
    return Math.max(...this.orders.map((o: AirlineOrder) => o.colors));
  }

  /**
   * The maximum width within the group
   */
  get maxWidth() {
    if (this._storedMaxWidth !== undefined) return this._storedMaxWidth;
    if (!this.orders || this.orders.length === 0) return 0;
    return Math.max(...this.orders.map((o: AirlineOrder) => o.width));
  }

  /**
   * The total costs of the airline order group
   */
  get originalTotalCosts() {
    if (this._storedOriginalTotalCosts !== undefined) return this._storedOriginalTotalCosts;
    return this.orders.reduce(
      (total: number, order: AirlineOrder) => total + order.originalTotalCosts,
      0,
    );
  }

  /**
   * The pricing table applicable to this order group
   */
  @Exclude()
  get pricingTable(): AirlinePricing | undefined {
    return this.findPricingTable(this.maxWidth);
  }

  /**
   * The saved costs of the order group
   */
  get savedCosts() {
    if (this._storedSavedCosts !== undefined) return this._storedSavedCosts;
    return this.originalTotalCosts - this.subtotalCosts;
  }

  /**
   * The total sale price of all orders in the group
   */
  get totalSalePrice() {
    if (this._storedTotalSalePrice !== undefined) return this._storedTotalSalePrice;
    return this.orders.reduce(
      (total: number, order: AirlineOrder) => total + order.salePriceLine,
      0,
    );
  }

  /**
   * The subtotal costs of the airline order group
   */
  get subtotalCosts() {
    if (this._storedSubtotalCosts !== undefined) return this._storedSubtotalCosts;
    return this.units * this.unitCosts;
  }

  /**
   * The switch costs of the airline order group
   */
  get switchCosts() {
    if (this._storedSwitchCosts !== undefined) return this._storedSwitchCosts;
    return (this.orders.length - 1) * this.settings.switchCosts;
  }

  /**
   * The total costs of the airline order group
   */
  get totalCosts() {
    if (this._storedTotalCosts !== undefined) return this._storedTotalCosts;
    return this.subtotalCosts + this.switchCosts;
  }



  /**
   * The profit amount of the airline order group
   * Formula: totalSalePrice - subtotalCosts - switchCosts
   */
  get profitAmount() {
    if (this._storedProfitAmount !== undefined) return this._storedProfitAmount;
    return this.totalSalePrice - this.subtotalCosts - this.switchCosts;
  }

  /**
   * The total costs of the airline order group
   */
  get profitPercentage() {
    if (this._storedProfitPercentage !== undefined) return this._storedProfitPercentage;
    return (this.profitAmount / (this.profitAmount + this.totalCosts)) * 100;
  }

  /**
   * The units of the airline order group
   */
  get units(): number {
    if (this._storedUnits !== undefined) return this._storedUnits;
    return this.orders.reduce(
      (total: number, order: AirlineOrder) => total + order.units,
      0,
    );
  }

  /**
   * The unit costs of the airline order group
   */
  get unitCosts() {
    if (this._storedUnitCosts !== undefined) return this._storedUnitCosts;
    return (
      this.pricingTable?.findUnitCosts(this.maxColors, this.units) ?? Infinity
    );
  }

  /**
   * The total base foil roll amount of the airline order group
   */
  get totalBaseFoilRollAmount(): number {
    if (this._storedTotalBaseFoilRollAmount !== undefined) return this._storedTotalBaseFoilRollAmount;
    return this.orders.reduce(
      (total: number, order: AirlineOrder) => total + (order.baseFoilRollAmount || 0),
      0,
    );
  }

  get baseFoilSKU(): string {
    if (this._storedBaseFoilSKU !== undefined) return this._storedBaseFoilSKU;
    const skus = new Set(this.orders.map(order => order.baseFoilSKU).filter(Boolean));
    if (skus.size > 1) {
      return "Warning: Multiple SKUs detected";
    }
    return Array.from(skus)[0] || "";
  }

  /// ///////////////////////////////////////
  /// ////////////// METHODS ////////////////
  /// ///////////////////////////////////////

  public async get(
    pathVars?:
      | string
      | string[]
      | {
          id?: string;
        }
      | undefined,
  ): Promise<AirlineOrderGroup> {
    return (await super.get(pathVars)) as AirlineOrderGroup;
  }

  /**
   * Find the unit costs for a given number of colors and units
   */
  findUnitCosts(colors: number, units: number, pricingValues: any) {
    let returnValue = 0,
      lowerUnitBound = 0;

    // Find lowest unit key
    const lowestUnitKey = Math.min(
      ...pricingValues.map((p: any) => parseInt(p.attributes.units)),
    );
    const lowestColorKey = Math.min(
      ...pricingValues.map((p: any) => parseInt(p.attributes.colors)),
    );

    // Failsafe
    if (units < lowestUnitKey) {
      units = lowestUnitKey;
    }
    if (colors < lowestColorKey) {
      colors = lowestColorKey;
    }

    // Find unit costs
    for (const { attributes, value } of pricingValues) {
      if (parseInt(attributes.colors) === parseInt(colors.toString())) {
        if (
          parseInt(attributes.units) <= units &&
          parseInt(attributes.units) > lowerUnitBound
        ) {
          returnValue = value.floatValue;
          lowerUnitBound = attributes.units;
        }
      }
    }

    return returnValue;
  }

  /**
   * Find the unit costs for a given number of colors and units
   */
  findPricingTable(width: number) {
    return (
      this.pricingTables.find((p: any) => p.width === width) ||
      this.pricingTables[this.pricingTables.length - 1]
    );
  }

  /**
   * Set the pricing tables
   * @param pricingTables The pricing tables
   * @returns {void}
   */
  setPricingTables(pricingTables: AirlinePricing[]) {
    this.pricingTables = pricingTables;
  }

  /// ///////////////////////////////////////
  /// /////////// STATIC METHODS ////////////
  /// ///////////////////////////////////////

  static override FromJSON(data?: JSONData): AirlineOrderGroup {
    return new AirlineOrderGroup(undefined, data);
  }

  static override FromJSONArray(data: JSONData[]): AirlineOrderGroup[] {
    return data.map((item) => AirlineOrderGroup.FromJSON(item));
  }

  static FromDto(dto: AirlineOrderGroupDto): AirlineOrderGroup {
    return new AirlineOrderGroup(undefined, dto.toJSON());
  }

  /**
   * Get all delivery address codes within the group
   * @param orderGroup The order group
   * @returns {number[]} The delivery address codes
   */
  static getDeliveryAddressCodes(orderGroup: AirlineOrderGroup): number[] {
    if (!orderGroup.orders) {
      return [];
    }

    return [
      ...new Set(
        orderGroup.orders.map((order: any) =>
          order.warehouse === "01" ? 1265 : parseInt(order.deliveryAddress),
        ),
      ),
    ];
  }
}
